import api from './api';
import { ApiResponse } from '../types';

export type ConcessionMode = 'percent' | 'flat';
export type ConcessionAppliesTo = 'all' | 'categories';
/** Null = applied at once; otherwise the second-person decision on it. */
export type ConcessionApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface FeeConcession {
  _id: string;
  schoolId: string;
  studentId: string;
  studentName?: string;
  grNumber?: string | null;
  label: string;
  mode: ConcessionMode;
  amount: number;
  appliesTo: ConcessionAppliesTo;
  feeCategoryIds: string[];
  categoryNames?: string[];
  validFrom: string;
  validTo: string | null;
  reason?: string;
  isActive: boolean;
  approvalStatus: ConcessionApprovalStatus | null;
  createdBy: string;
  createdByName?: string | null;
  decidedAt?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
  revokedAt?: string | null;
  revocationReason?: string;
}

export interface ListFilter {
  studentId?: string;
  includeInactive?: boolean;
  approvalStatus?: ConcessionApprovalStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ListResponse {
  concessions: FeeConcession[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

export const listConcessions = async (filter: ListFilter = {}): Promise<ListResponse> => {
  const sp = new URLSearchParams();
  if (filter.studentId)              sp.append('studentId', filter.studentId);
  if (filter.includeInactive)        sp.append('includeInactive', 'true');
  if (filter.approvalStatus)         sp.append('approvalStatus', filter.approvalStatus);
  if (filter.search)                 sp.append('search', filter.search);
  if (filter.page)                   sp.append('page', String(filter.page));
  if (filter.limit)                  sp.append('limit', String(filter.limit));
  const r = await api.get<ApiResponse<ListResponse>>(`/fee-concessions?${sp.toString()}`);
  if (!r.data.success) throw new Error(r.data.message);
  return r.data.data!;
};

export interface CreateConcessionInput {
  studentId: string;
  label: string;
  mode: ConcessionMode;
  amount: number;
  appliesTo: ConcessionAppliesTo;
  feeCategoryIds?: string[];
  validFrom: string;
  validTo?: string | null;
  reason?: string;
}

export const createConcession = async (input: CreateConcessionInput): Promise<FeeConcession> => {
  const r = await api.post<ApiResponse<FeeConcession>>('/fee-concessions', input);
  if (!r.data.success) throw new Error(r.data.message);
  return r.data.data!;
};

export interface BulkConcessionInput {
  studentIds: string[];
  label: string;
  mode: ConcessionMode;
  amount: number;
  appliesTo: ConcessionAppliesTo;
  feeCategoryIds?: string[];
  validFrom: string;
  validTo?: string | null;
  reason?: string;
}

export interface BulkConcessionResult {
  /** True when they wait for a second person in Finance → Approvals. */
  pending: boolean;
  created: number;
  skipped: number;
  skippedStudentIds: string[];
  concessionIds: string[];
}

export const createBulkConcession = async (input: BulkConcessionInput): Promise<BulkConcessionResult> => {
  const r = await api.post<ApiResponse<BulkConcessionResult>>('/fee-concessions/bulk', input);
  if (!r.data.success) throw new Error(r.data.message);
  return r.data.data!;
};

export const revokeConcession = async (id: string, reason?: string): Promise<FeeConcession> => {
  const r = await api.post<ApiResponse<FeeConcession>>(`/fee-concessions/${id}/revoke`, { reason });
  if (!r.data.success) throw new Error(r.data.message);
  return r.data.data!;
};

export const approveConcession = async (id: string): Promise<FeeConcession> => {
  const r = await api.post<ApiResponse<FeeConcession>>(`/fee-concessions/${id}/approve`);
  if (!r.data.success) throw new Error(r.data.message);
  return r.data.data!;
};

export const rejectConcession = async (id: string, reason: string): Promise<FeeConcession> => {
  const r = await api.post<ApiResponse<FeeConcession>>(`/fee-concessions/${id}/reject`, { reason });
  if (!r.data.success) throw new Error(r.data.message);
  return r.data.data!;
};

const feeConcessionService = {
  listConcessions,
  createConcession,
  createBulkConcession,
  revokeConcession,
  approveConcession,
  rejectConcession,
};

export default feeConcessionService;
